import React from "react";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

export function Input({
  label,
  error,
  helperText,
  icon,
  fullWidth = true,
  className = "",
  id,
  ...props
}: InputProps) {
  const generatedId = React.useId();
  const inputId = id || `input-${generatedId}`;

  return (
    <div className={`${fullWidth ? "w-full" : ""}`}>
      {label && (
        <label
          htmlFor={inputId}
          className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700"
        >
          {icon && <span className="text-primary-600">{icon}</span>}
          <span>{label}</span>
          {props.required && <span className="text-rose-500">*</span>}
        </label>
      )}
      <input
        id={inputId}
        className={`
          w-full px-4 py-3 text-sm font-semibold
          bg-white border rounded-xl
          transition-all duration-200
          focus:outline-none focus:ring-4
          disabled:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-500
          placeholder:text-slate-400
          ${
            error
              ? "border-rose-300 focus:border-rose-500 focus:ring-rose-100"
              : "border-slate-200 hover:border-primary-300 focus:border-primary-400 focus:ring-primary-100"
          }
          ${className}
        `
          .trim()
          .replace(/\s+/g, " ")}
        aria-invalid={!!error}
        aria-describedby={
          error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
        }
        {...props}
      />
      {error && (
        <p
          id={`${inputId}-error`}
          className="mt-2 flex items-center gap-1.5 text-sm font-medium text-rose-600"
        >
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span>{error}</span>
        </p>
      )}
      {helperText && !error && (
        <p id={`${inputId}-helper`} className="mt-2 text-sm text-slate-500">
          {helperText}
        </p>
      )}
    </div>
  );
}

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

export function Textarea({
  label,
  error,
  helperText,
  icon,
  fullWidth = true,
  className = "",
  id,
  ...props
}: TextareaProps) {
  const generatedId = React.useId();
  const textareaId = id || `textarea-${generatedId}`;

  return (
    <div className={`${fullWidth ? "w-full" : ""}`}>
      {label && (
        <label
          htmlFor={textareaId}
          className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700"
        >
          {icon && <span className="text-primary-600">{icon}</span>}
          <span>{label}</span>
          {props.required && <span className="text-rose-500">*</span>}
        </label>
      )}
      <textarea
        id={textareaId}
        className={`
          w-full px-4 py-3 text-sm font-semibold
          bg-white border rounded-xl
          transition-all duration-200
          focus:outline-none focus:ring-4
          disabled:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-500
          placeholder:text-slate-400
          resize-y min-h-[100px]
          ${
            error
              ? "border-rose-300 focus:border-rose-500 focus:ring-rose-100"
              : "border-slate-200 hover:border-primary-300 focus:border-primary-400 focus:ring-primary-100"
          }
          ${className}
        `
          .trim()
          .replace(/\s+/g, " ")}
        aria-invalid={!!error}
        aria-describedby={
          error ? `${textareaId}-error` : helperText ? `${textareaId}-helper` : undefined
        }
        {...props}
      />
      {error && (
        <p
          id={`${textareaId}-error`}
          className="mt-2 flex items-center gap-1.5 text-sm font-medium text-rose-600"
        >
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span>{error}</span>
        </p>
      )}
      {helperText && !error && (
        <p id={`${textareaId}-helper`} className="mt-2 text-sm text-slate-500">
          {helperText}
        </p>
      )}
    </div>
  );
}
